import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Parallax Hook
const useParallax = () => {
  useEffect(() => {
    const handleScroll = () => {
      const images = document.querySelectorAll('.parallax-image');
      images.forEach(img => {
        const rect = img.getBoundingClientRect();
        const scrolled = window.scrollY;
        const rate = (rect.top + scrolled) * 0.08;
        img.style.transform = `translateY(${rate - scrolled * 0.08}px)`;
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
};

// Dramatic Enter Transition Overlay
const EnterTransition = ({ isActive, onComplete }) => {
  useEffect(() => {
    if (isActive) {
      // Play swoosh sound
      const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3');
      audio.volume = 0.4;
      audio.play().catch(() => {});
      
      const timer = setTimeout(onComplete, 2500);
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Flash effect */}
      <div className="absolute inset-0 bg-[#C5A059]/10 animate-flash" />
      
      {/* Closing curtains */}
      <div className="absolute top-0 left-0 w-1/2 h-full bg-[#0a1018] animate-curtain-left" />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-[#0a1018] animate-curtain-right" />
      
      {/* Center glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-full bg-gradient-to-b from-transparent via-[#C5A059]/50 to-transparent animate-glow-line" />
    </div>
  );
};

// Subtle Gold Ambient Glow - elegant pulsating shimmer across entire page
const AmbientGlow = () => {
  return (
    <div className="fixed inset-0 pointer-events-none z-10">
      {/* Primary central glow */}
      <div 
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full animate-ambient-pulse"
        style={{
          background: 'radial-gradient(circle, rgba(197,160,89,0.04) 0%, transparent 60%)',
        }}
      />
      {/* Secondary glow - lower */}
      <div 
        className="absolute top-[60%] left-1/3 w-[900px] h-[900px] rounded-full animate-ambient-pulse-delayed"
        style={{
          background: 'radial-gradient(circle, rgba(197,160,89,0.03) 0%, transparent 55%)',
        }}
      />
      {/* Third glow - bottom area */}
      <div 
        className="absolute top-[85%] right-1/4 w-[800px] h-[800px] rounded-full animate-ambient-pulse"
        style={{
          background: 'radial-gradient(circle, rgba(197,160,89,0.035) 0%, transparent 50%)',
          animationDelay: '4s',
        }}
      />
    </div>
  );
};

// Strong cinematic vignette
const CinematicVignette = () => {
  return (
    <div 
      className="fixed inset-0 pointer-events-none z-20"
      style={{
        background: `
          radial-gradient(ellipse at center, transparent 30%, rgba(5,6,7,0.4) 70%, rgba(5,6,7,0.85) 100%)
        `,
      }}
    />
  );
};

// Removed - particles replaced with ambient glow
const GlobalFloatingParticles = () => null;
const FloatingParticles = () => null;

// Exclusive Counter Badge
const ExclusiveBadge = () => {
  return (
    <div className="text-right opacity-80 mt-12">
      <p className="text-[9px] tracking-[0.3em] uppercase text-[#C5A059]/40 mb-1">Portfolio</p>
      <p className="text-2xl font-light text-[#F1F5F9]/25" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <span className="text-[#C5A059]/50">12</span>
        <span className="text-[10px] tracking-widest ml-2 text-[#F1F5F9]/20">OBJEKTE</span>
      </p>
    </div>
  );
};

// Hero Section
const HeroSection = ({ onEnter, isLocked }) => {
  const [loaded, setLoaded] = useState(false);
  const [hovered, setHovered] = useState(false);
  
  useEffect(() => {
    setTimeout(() => setLoaded(true), 200);
  }, []);

  // Lock body scroll and prevent scroll when page is locked
  useEffect(() => {
    const preventScroll = (e) => {
      if (isLocked) {
        e.preventDefault();
        window.scrollTo(0, 0);
      }
    };
    
    if (isLocked) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
      window.scrollTo(0, 0);
      window.addEventListener('scroll', preventScroll, { passive: false });
      window.addEventListener('wheel', preventScroll, { passive: false });
      window.addEventListener('touchmove', preventScroll, { passive: false });
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.height = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
      document.body.style.height = 'auto';
      window.removeEventListener('scroll', preventScroll);
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);
    };
  }, [isLocked]);

  return (
    <section 
      className="relative min-h-screen flex flex-col justify-center items-center overflow-hidden bg-[#0a1018]"
      data-testid="hero-section"
    >
      {/* Epic background */}
      <div 
        className="absolute inset-0 transition-transform duration-[3s] ease-out"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1596481285816-60c474a516b9?q=80&w=2000&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.4,
          filter: 'contrast(1.1) brightness(0.85)',
          transform: hovered ? 'scale(1.02)' : 'scale(1)',
        }}
      />
      
      {/* Floating particles */}
      <FloatingParticles />
      
      {/* Gradients */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1018] via-transparent to-[#0a1018]/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a1018]/40 via-transparent to-[#0a1018]/40" />
      
      {/* Animated gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C5A059]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C5A059]/20 to-transparent" />
      
      {/* Content */}
      <div className="relative z-10 text-center px-6 md:px-12 lg:px-24 max-w-5xl">
        {/* Brand - prominent */}
        <div className={`mb-20 transition-all duration-[2.5s] ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-6'}`}>
          <p className="text-lg md:text-xl lg:text-2xl tracking-[0.35em] uppercase text-[#C5A059]/80 mb-5 font-light">
            Grahammer Real Estate
          </p>
          <div className="w-20 h-[1px] bg-gradient-to-r from-transparent via-[#C5A059]/50 to-transparent mx-auto" />
        </div>
        
        {/* Tagline */}
        <h1 
          className={`text-5xl md:text-7xl lg:text-[6.5rem] tracking-[-0.03em] font-light text-[#F1F5F9] mb-3 transition-all duration-[2s] delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          <span className="italic font-extralight">For those</span>
        </h1>
        <h1 
          className={`text-5xl md:text-7xl lg:text-[6.5rem] tracking-[-0.03em] font-light text-[#F1F5F9]/50 mb-20 transition-all duration-[2s] delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          <span className="italic font-extralight">who know.</span>
        </h1>
        
        {/* CTA */}
        <div className={`transition-all duration-[2s] delay-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
          <Button
            onClick={onEnter}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className="group relative bg-transparent border border-[#C5A059]/30 text-[#C5A059]/80 hover:border-[#C5A059]/60 hover:text-[#C5A059] px-16 py-7 text-[10px] tracking-[0.4em] uppercase rounded-none transition-all duration-700 overflow-hidden hover:shadow-[0_0_30px_rgba(197,160,89,0.15)]"
            data-testid="hero-cta-btn"
          >
            <span className="relative z-10">Eintreten</span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#C5A059]/0 via-[#C5A059]/10 to-[#C5A059]/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
          </Button>
        </div>
      </div>
      
      {/* Corner accents */}
      <div className={`absolute top-8 left-8 w-24 h-24 border-l border-t border-[#C5A059]/20 transition-all duration-[3s] delay-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`} />
      <div className={`absolute bottom-8 right-8 w-24 h-24 border-r border-b border-[#C5A059]/20 transition-all duration-[3s] delay-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`} />
      <div className={`absolute top-8 right-8 w-24 h-24 border-r border-t border-[#C5A059]/10 transition-all duration-[3s] delay-1200 ${loaded ? 'opacity-100' : 'opacity-0'}`} />
      <div className={`absolute bottom-8 left-8 w-24 h-24 border-l border-b border-[#C5A059]/10 transition-all duration-[3s] delay-1200 ${loaded ? 'opacity-100' : 'opacity-0'}`} />
    </section>
  );
};

// Philosophy Section
const PhilosophySection = () => {
  return (
    <section 
      id="philosophy"
      className="relative py-32 md:py-48 bg-[#0e1522] overflow-hidden"
      data-testid="philosophy-section"
    >
      <FloatingParticles />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12 items-center">
          
          {/* Left - Image with Parallax */}
          <div className="lg:col-span-5 relative">
            <div className="relative overflow-hidden group parallax-container">
              <img 
                src="https://images.unsplash.com/photo-1685390981768-5d20dd3f35d8?q=80&w=1000&auto=format&fit=crop"
                alt=""
                className="w-full h-[550px] object-cover transition-transform duration-[2s] group-hover:scale-105 parallax-image"
                style={{ filter: 'contrast(1.05) brightness(0.9)' }}
                loading="lazy"
              />
              <div className="absolute inset-4 border border-[#C5A059]/15 pointer-events-none transition-all duration-700 group-hover:inset-6" />
            </div>
            {/* Portfolio Badge moved here */}
            <ExclusiveBadge />
          </div>
          
          {/* Right - Text */}
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#C5A059]/50 mb-8">
              Philosophie
            </p>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-light text-[#F1F5F9]/90 mb-8 leading-[1.2]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Kein Angebot.<br/>
              <span className="italic text-[#F1F5F9]/50">Eine Einladung.</span>
            </h2>
            <div className="w-10 h-[1px] bg-[#C5A059]/30 mb-8" />
            <p className="text-sm md:text-base leading-[2] tracking-wide font-light text-[#F1F5F9]/35 max-w-md mb-6">
              Unser Portfolio bleibt im Verborgenen. Unsere Klienten bleiben außergewöhnlich.
              In einer Welt, die zu viel spricht, bieten wir das seltenste Gut: Stille.
            </p>
            <p className="text-sm md:text-base leading-[2] tracking-wide font-light text-[#F1F5F9]/35 max-w-md mb-8">
              Wir arbeiten ausschließlich mit ausgewählten Partnern und Institutionen – 
              ein Netzwerk, das keine Lautstärke braucht.
            </p>
            <p 
              className="text-base md:text-lg italic text-[#C5A059]/70"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Nicht für jeden. Nur für die Richtigen.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Standards Section - Single Epic Image
const StandardsSection = () => {
  return (
    <section 
      className="relative py-32 md:py-48 bg-[#0c131d]"
      data-testid="standards-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12 items-center">
          
          {/* Left - Text */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#C5A059]/50 mb-8">
              Anspruch
            </p>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-light text-[#F1F5F9]/90 mb-8 leading-[1.2]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Exzellenz<br/>
              <span className="italic text-[#F1F5F9]/50">ohne Ausnahme.</span>
            </h2>
            <div className="w-10 h-[1px] bg-[#C5A059]/30 mb-8" />
            <p className="text-sm md:text-base leading-[2] tracking-wide font-light text-[#F1F5F9]/35 max-w-md mb-8">
              Was wir vertreten, hat Geschichte. Was wir vermitteln, schreibt neue. 
              Unsere Maßstäbe existieren jenseits dessen, was sich in Zahlen fassen lässt.
            </p>
            <p 
              className="text-base md:text-lg italic text-[#C5A059]/70"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Wer sucht, wird nicht finden. Wer bereit ist, wird gefunden.
            </p>
          </div>
          
          {/* Right - Single large image with Parallax: elegant gold door lock */}
          <div className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
            <div className="relative overflow-hidden group parallax-container">
              <img 
                src="https://images.unsplash.com/photo-1702573848674-1ebc487abe87?q=80&w=1000&auto=format&fit=crop"
                alt=""
                className="w-full h-[550px] object-cover transition-transform duration-[2s] group-hover:scale-105 parallax-image"
                style={{ filter: 'contrast(1.05) brightness(0.9)' }}
                loading="lazy"
              />
              {/* Dark gradient overlay to blend with page */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c131d]/70 via-transparent to-[#0c131d]/40" />
              <div className="absolute inset-0 bg-gradient-to-l from-[#0c131d]/50 via-transparent to-transparent" />
              {/* Gold frame accent */}
              <div className="absolute inset-4 border border-[#C5A059]/15 pointer-events-none transition-all duration-700 group-hover:inset-6" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Inquiry Form Section
const InquirySection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message
      });
      
      toast.success("Ihre Anfrage wurde entgegengenommen.", {
        duration: 5000,
      });
      
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Inquiry submission error:', error);
      toast.error("Übermittlung fehlgeschlagen. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="inquiry"
      className="relative py-32 md:py-48 bg-[#0e1522] overflow-hidden"
      data-testid="inquiry-section"
    >
      <FloatingParticles />
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C5A059]/15 to-transparent" />
      
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#C5A059]/50 mb-6">
            Kontakt
          </p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light text-[#F1F5F9]/90 mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span className="italic">Visionen teilen.</span>
          </h2>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light text-[#F1F5F9]/50 mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span className="italic">Partnerschaft leben.</span>
          </h2>
          <p className="text-sm md:text-base text-[#F1F5F9]/40 font-light tracking-wide max-w-md mx-auto mb-3">
            Wir haben die Möglichkeiten.
          </p>
          <p className="text-sm md:text-base text-[#F1F5F9]/30 font-light tracking-wide max-w-lg mx-auto">
            Lassen Sie uns herausfinden, was wir gemeinsam erreichen können.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-10" data-testid="inquiry-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="relative">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onFocus={() => setFocusedField('name')}
                onBlur={() => setFocusedField(null)}
                placeholder="Name"
                className="w-full bg-transparent border-0 border-b border-[#F1F5F9]/10 py-4 text-sm text-[#F1F5F9]/70 placeholder-[#F1F5F9]/25 focus:outline-none transition-colors duration-500 tracking-wide"
                required
                data-testid="inquiry-name-input"
              />
              <div className={`absolute bottom-0 left-0 h-[1px] bg-[#C5A059]/70 transition-all duration-700 ease-out ${focusedField === 'name' ? 'w-full' : 'w-0'}`} />
            </div>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                placeholder="E-Mail"
                className="w-full bg-transparent border-0 border-b border-[#F1F5F9]/10 py-4 text-sm text-[#F1F5F9]/70 placeholder-[#F1F5F9]/25 focus:outline-none transition-colors duration-500 tracking-wide"
                required
                data-testid="inquiry-email-input"
              />
              <div className={`absolute bottom-0 left-0 h-[1px] bg-[#C5A059]/70 transition-all duration-700 ease-out ${focusedField === 'email' ? 'w-full' : 'w-0'}`} />
            </div>
          </div>
          
          <div className="relative">
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onFocus={() => setFocusedField('phone')}
              onBlur={() => setFocusedField(null)}
              placeholder="Telefon (optional)"
              className="w-full bg-transparent border-0 border-b border-[#F1F5F9]/10 py-4 text-sm text-[#F1F5F9]/70 placeholder-[#F1F5F9]/25 focus:outline-none transition-colors duration-500 tracking-wide"
              data-testid="inquiry-phone-input"
            />
            <div className={`absolute bottom-0 left-0 h-[1px] bg-[#C5A059]/70 transition-all duration-700 ease-out ${focusedField === 'phone' ? 'w-full' : 'w-0'}`} />
          </div>
          
          <div className="relative">
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              onFocus={() => setFocusedField('message')}
              onBlur={() => setFocusedField(null)}
              placeholder="Ihre Anfrage"
              rows={3}
              className="w-full bg-transparent border-0 border-b border-[#F1F5F9]/10 py-4 text-sm text-[#F1F5F9]/70 placeholder-[#F1F5F9]/25 focus:outline-none transition-colors duration-500 tracking-wide resize-none"
              required
              data-testid="inquiry-message-input"
            />
            <div className={`absolute bottom-0 left-0 h-[1px] bg-[#C5A059]/70 transition-all duration-700 ease-out ${focusedField === 'message' ? 'w-full' : 'w-0'}`} />
          </div>
          
          <div className="text-center pt-8">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="group relative bg-transparent border border-[#C5A059]/25 text-[#C5A059]/70 hover:border-[#C5A059]/50 hover:text-[#C5A059] px-16 py-6 text-[10px] tracking-[0.4em] uppercase rounded-none transition-all duration-700 disabled:opacity-30 overflow-hidden hover:shadow-[0_0_20px_rgba(197,160,89,0.1)]"
              data-testid="inquiry-submit-btn"
            >
              <span className="relative z-10">{isSubmitting ? 'Wird gesendet...' : 'Absenden'}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#C5A059]/0 via-[#C5A059]/5 to-[#C5A059]/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer 
      className="py-20 px-6 md:px-12 lg:px-24 bg-[#0a1018]"
      data-testid="footer-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="h-[1px] bg-gradient-to-r from-transparent via-[#C5A059]/10 to-transparent mb-20" />
        
        <div className="flex flex-col items-center">
          {/* Brand */}
          <div className="text-center">
            <p 
              className="text-base font-light text-[#F1F5F9]/40 tracking-[0.2em] uppercase"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Grahammer Real Estate
            </p>
            <p className="text-[10px] text-[#F1F5F9]/20 tracking-[0.15em] mt-2">
              Teil der Grahammer Capital Group
            </p>
          </div>
        </div>
        
        <div className="mt-20 text-center">
          <p className="text-[9px] text-[#F1F5F9]/20 tracking-[0.3em] uppercase">
            Seit 2020
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  const [transitioning, setTransitioning] = useState(false);
  const [entered, setEntered] = useState(false);
  const [pageLocked, setPageLocked] = useState(true);

  // Enable parallax effect
  useParallax();

  const handleEnter = () => {
    setTransitioning(true);
  };

  const handleTransitionComplete = () => {
    setEntered(true);
    setPageLocked(false);
    setTimeout(() => {
      document.getElementById('philosophy')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div className="bg-[#0a1018] min-h-screen">
      {/* Ambient Gold Glow */}
      <AmbientGlow />
      
      {/* Cinematic Vignette */}
      <CinematicVignette />
      
      {/* Grain Overlay */}
      <div className="grain-overlay" />
      
      {/* Enter Transition */}
      <EnterTransition isActive={transitioning && !entered} onComplete={handleTransitionComplete} />
      
      {/* Toaster */}
      <Toaster 
        position="bottom-center" 
        toastOptions={{
          style: {
            background: '#0e1522',
            color: '#F1F5F9',
            border: '1px solid rgba(197, 160, 89, 0.1)',
            fontSize: '12px',
            letterSpacing: '0.05em',
          },
        }}
      />
      
      {/* Sections */}
      <HeroSection onEnter={handleEnter} isLocked={pageLocked} />
      <PhilosophySection />
      <StandardsSection />
      <InquirySection />
      <Footer />
    </div>
  );
}

export default App;
